import { useState } from 'react';
import { useServiceAvailable } from '../hooks/useServiceOrigin';
import './LoginScreen.css';

interface Props {
  onGoogleLogin: () => void;
  onEmailLogin: (email: string, password: string) => Promise<void>;
  error: string;
}

export function LoginScreen({ onGoogleLogin, onEmailLogin, error }: Props) {
  // Deklariert die Instanz KEINEN Anmeldedienst (auth: null im Dienst-Vertrag
  // 77eb0c9), sind die Login-Knöpfe eine Sackgasse: Jeder Klick liefe leise
  // ins Leere (die Aufruf-Guards unterbinden den Request). Ein Portal ohne
  // auth ist laut Design nicht betriebsfähig — das ist ein EINRICHTUNGS-
  // Zustand, kein Login-Fehler, und genau so wird er benannt.
  const authAvailable = useServiceAvailable('auth');
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setEmailError('');
    try {
      await onEmailLogin(email, password);
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Login fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  if (!authAvailable) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <div className="login-logo">
            Agent<span>OS</span>
          </div>
          <div className="login-subtitle">Session Manager</div>
          <p className="login-setup-note">
            Diese Instanz hat noch keinen Anmeldedienst konfiguriert — die
            Einrichtung ist unvollständig. Bitte wenden Sie sich an die Person,
            die diese Instanz betreibt.
          </p>
          {/* Für die einrichtende Person, nicht für den Endnutzer: benennt die
              fehlende Variable. Das Backend-Detail (hart kodierte Defaults bis
              zur auth-Entscheidung, Clouds 18-Stellen-Befund) gehört in die
              Betreiber-Doku und den Bootstrap-Output — nicht auf einen Screen,
              auf dem ein Endnutzer ohne Handlungsmöglichkeit steht. */}
          <p className="login-setup-tech">
            Einrichtung: <code>AUTH_API_URL</code> ist auf dieser Instanz nicht gesetzt.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-logo">
          Agent<span>OS</span>
        </div>
        <div className="login-subtitle">Session Manager</div>

        <button className="login-google-btn" onClick={onGoogleLogin}>
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </button>

        <button
          type="button"
          className="login-toggle-email"
          onClick={() => { setShowEmail((v) => !v); setEmailError(''); }}
        >
          {showEmail ? 'Email-Login verbergen' : 'Login mit Email + Passwort'}
        </button>

        {showEmail && (
          <form className="login-email-form" onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              disabled={submitting}
            />
            <input
              type="password"
              placeholder="Passwort"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={submitting}
            />
            <button
              type="submit"
              className="login-submit-btn"
              disabled={submitting || !email || !password}
            >
              {submitting ? 'Login…' : 'Anmelden'}
            </button>
            {emailError && <div className="login-error">{emailError}</div>}
          </form>
        )}

        {error && !emailError && <div className="login-error">{error}</div>}
      </div>
    </div>
  );
}
